import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { usePlatformSettings } from '../../contexts/PlatformSettingsContext';
import { 
  SettingsIcon,
  SaveIcon,
  ShieldIcon,
  MailIcon,
  CreditCardIcon,
  DatabaseIcon,
  PaletteIcon,
  BellIcon,
  PlugIcon,
  GlobeIcon,
  LockIcon,
  ServerIcon,
  ImageIcon,
  CodeIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  UploadIcon,
  EyeIcon,
  EyeOffIcon,
  InfoIcon,
  RefreshCwIcon,
  ToggleLeftIcon,
  ToggleRightIcon,
  ClockIcon,
  Banknote as BanknoteIcon,
  KeyIcon,
  WifiIcon,
  MessageSquareIcon,
  ZapIcon,
  ActivityIcon,
  CopyIcon,
  CoinsIcon,
  TrashIcon,
  PlusIcon,
  DownloadIcon,
  XIcon
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  {
    id: 'general',
    name: 'Genel Ayarlar',
    icon: SettingsIcon,
    color: 'text-blue-600',
    bg: 'bg-blue-50'
  },
  {
    id: 'security',
    name: 'Güvenlik',
    icon: ShieldIcon,
    color: 'text-red-600',
    bg: 'bg-red-50'
  },
  {
    id: 'email',
    name: 'Email Ayarları',
    icon: MailIcon,
    color: 'text-green-600',
    bg: 'bg-green-50'
  },
  {
    id: 'payment',
    name: 'Ödeme Sistemi',
    icon: CreditCardIcon,
    color: 'text-purple-600',
    bg: 'bg-purple-50'
  },
  {
    id: 'system',
    name: 'Sistem',
    icon: ServerIcon,
    color: 'text-orange-600',
    bg: 'bg-orange-50'
  },
  {
    id: 'branding',
    name: 'Branding & UI',
    icon: PaletteIcon,
    color: 'text-pink-600',
    bg: 'bg-pink-50'
  },
  {
    id: 'notifications',
    name: 'Bildirimler',
    icon: BellIcon,
    color: 'text-yellow-600',
    bg: 'bg-yellow-50'
  },
  {
    id: 'currencies',
    name: 'Para Birimleri',
    icon: BanknoteIcon,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50'
  },
  {
    id: 'integrations',
    name: 'Entegrasyonlar',
    icon: PlugIcon,
    color: 'text-indigo-600',
    bg: 'bg-indigo-50'
  }
];

const PasskeyGenerator = () => {
  const [passkey, setPasskey] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (expiresAt) {
      const interval = setInterval(() => {
        const now = new Date();
        const expires = new Date(expiresAt);
        const secondsLeft = Math.max(0, Math.floor((expires - now) / 1000));
        setTimeLeft(secondsLeft);
        
        if (secondsLeft === 0) {
          setPasskey('');
          setExpiresAt(null);
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [expiresAt]);

  const generatePasskey = async () => {
    setGenerating(true);
    try {
      const ultra_admin_token = localStorage.getItem('ultra_admin_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/ultra-admin/passkey/generate`,
        {},
        { headers: { 'Authorization': `Bearer ${ultra_admin_token}` } }
      );
      
      setPasskey(response.data.passkey);
      setExpiresAt(response.data.expires_at);
      setTimeLeft(response.data.valid_for_seconds);
      
      toast.success('Passkey oluşturuldu! 90 saniye geçerli.', { duration: 5000 });
    } catch (error) {
      toast.error('Passkey oluşturulurken hata oluştu');
    } finally {
      setGenerating(false);
    }
  };

  const copyPasskey = () => {
    navigator.clipboard.writeText(passkey);
    toast.success('Passkey kopyalandı!');
  };

  return (
    <div className="space-y-4">
      {!passkey ? (
        <Button 
          onClick={generatePasskey} 
          disabled={generating}
          className="bg-purple-600 hover:bg-purple-700"
        >
          {generating ? (
            <>
              <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
              Oluşturuluyor...
            </>
          ) : (
            <>
              <KeyIcon className="w-4 h-4 mr-2" />
              Passkey Oluştur
            </>
          )}
        </Button>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="flex-1 bg-white border-2 border-purple-300 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Passkey</p>
                  <p className="text-2xl font-mono font-bold text-purple-600 tracking-wider">{passkey}</p>
                </div>
                <Button variant="outline" size="sm" onClick={copyPasskey}>
                  <CopyIcon className="w-4 h-4 mr-1" />
                  Kopyala
                </Button>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <ClockIcon className="w-5 h-5 text-orange-600" />
              <span className="text-sm text-orange-800">
                Kalan süre: <strong className="text-lg font-mono">{timeLeft}</strong> saniye
              </span>
            </div>
            {timeLeft <= 30 && (
              <AlertTriangleIcon className="w-5 h-5 text-orange-600 animate-pulse" />
            )}
          </div>
          
          <p className="text-xs text-gray-600 italic">
            ⚠️ Bu passkey tek kullanımlıktır. Kullandıktan sonra geçersiz olacaktır.
          </p>
        </div>
      )}
    </div>
  );
};

const UltraPlatformSettings = () => {
  const { refreshSettings, applyBrandingSettings } = usePlatformSettings();
  const [activeCategory, setActiveCategory] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  
  // Currency management states
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [processingCurrency, setProcessingCurrency] = useState(false);
  const [importingFile, setImportingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showAddCurrencyModal, setShowAddCurrencyModal] = useState(false);
  const [newCurrency, setNewCurrency] = useState({ code: '', symbol: '', name: '' });
  
  // Settings state
  const [settings, setSettings] = useState({
    // General
    platform_name: 'AdvanceCash.io',
    platform_tagline: 'Çalışan Avans Yönetim Platformu',
    support_email: 'support@advancecash.io',
    support_phone: '+90 555 123 4567',
    default_language: 'tr',
    default_timezone: 'Europe/Istanbul',
    maintenance_mode: false,
    maintenance_message: 'Sistem bakımda. Lütfen daha sonra tekrar deneyin.',
    
    // Security
    password_min_length: 8,
    password_require_uppercase: true,
    password_require_lowercase: true,
    password_require_numbers: true,
    password_require_special: true,
    password_expiry_days: 90,
    enable_2fa: false,
    force_2fa_for_admins: false,
    session_timeout_minutes: 1440, // 24 hours
    max_login_attempts: 5,
    lockout_duration_minutes: 30,
    
    // Email
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_encryption: 'tls',
    email_from_address: 'noreply@advancecash.io',
    email_from_name: 'AdvanceCash.io',
    
    // Payment
    stripe_publishable_key: '',
    stripe_secret_key: '',
    default_currency: 'TRY',
    tax_rate: 20,
    enable_trial: true,
    trial_days: 14,
    
    // System
    database_backup_enabled: true,
    backup_frequency: 'daily',
    backup_retention_days: 30,
    log_retention_days: 90,
    cache_enabled: true,
    cache_ttl_seconds: 3600,
    api_rate_limit_per_minute: 100,
    
    // Branding
    primary_color: '#7C3AED',
    secondary_color: '#4F46E5',
    logo_url: '',
    favicon_url: '',
    custom_css: '',
    footer_text: '© 2025 AdvanceCash.io - Tüm hakları saklıdır',
    
    // Notifications
    enable_email_notifications: true,
    enable_sms_notifications: false,
    enable_push_notifications: false,
    webhook_url: '',
    
    // Integrations
    whatsapp_business_api_key: '',
    sms_provider: 'none',
    sms_api_key: '',
    analytics_enabled: false,
    google_analytics_id: ''
  });

  const [showPasswordFields, setShowPasswordFields] = useState({
    smtp_password: false,
    stripe_secret_key: false,
    whatsapp_business_api_key: false,
    sms_api_key: false
  });

  useEffect(() => {
    loadSettings();
    // Don't fetch currencies on mount - only when currency category is opened
  }, []);

  // Fetch currencies only when currency category is active
  useEffect(() => {
    if (activeCategory === 'currencies' && currencies.length === 0) {
      fetchCurrencies();
    }
  }, [activeCategory]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const ultra_admin_token = localStorage.getItem('ultra_admin_token');
      
      if (!ultra_admin_token) {
        toast.error('Ultra Admin token bulunamadı');
        return;
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/ultra-admin/platform-settings`, {
        headers: {
          'Authorization': `Bearer ${ultra_admin_token}`
        }
      });
      
      if (response.data) {
        setSettings(response.data);
        // Reset preview state when loading fresh settings
        setIsPreviewing(false);
        setOriginalSettings(null);
        toast.success('Platform ayarları yüklendi');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error(error.response?.data?.detail || 'Ayarlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setUnsavedChanges(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const ultra_admin_token = localStorage.getItem('ultra_admin_token');
      
      if (!ultra_admin_token) {
        toast.error('Ultra Admin token bulunamadı');
        return;
      }
      
      const response = await axios.put(
        `${BACKEND_URL}/api/ultra-admin/platform-settings`,
        settings,
        {
          headers: {
            'Authorization': `Bearer ${ultra_admin_token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success('Ayarlar başarıyla kaydedildi');
      setUnsavedChanges(false);
      
      // Reset preview state
      if (isPreviewing) {
        setIsPreviewing(false);
        setOriginalSettings(null);
      }
      
      // Apply branding settings immediately
      applyBrandingSettings(settings);
      
      // Refresh platform settings context
      await refreshSettings();
      
      toast.success('🎨 Tema değişiklikleri uygulandı!', {
        duration: 3000
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Ayarlar kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const testEmailSettings = async () => {
    try {
      const ultra_admin_token = localStorage.getItem('ultra_admin_token');
      
      if (!ultra_admin_token) {
        toast.error('Ultra Admin token bulunamadı');
        return;
      }
      
      if (!settings.email_from_address) {
        toast.error('Lütfen önce email ayarlarını yapın');
        return;
      }
      
      toast.info('Test email gönderiliyor...');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/ultra-admin/test-email`,
        { test_email: settings.email_from_address },
        {
          headers: {
            'Authorization': `Bearer ${ultra_admin_token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success(response.data.message || 'Test email başarıyla gönderildi!');
    } catch (error) {
      console.error('Test email error:', error);
      toast.error(error.response?.data?.detail || 'Email gönderilemedi. Ayarları kontrol edin.');
    }
  };

  const testWebhook = async () => {
    try {
      const ultra_admin_token = localStorage.getItem('ultra_admin_token');
      
      if (!ultra_admin_token) {
        toast.error('Ultra Admin token bulunamadı');
        return;
      }
      
      if (!settings.webhook_url) {
        toast.error('Lütfen önce webhook URL girin');
        return;
      }
      
      toast.info('Webhook test ediliyor...');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/ultra-admin/test-webhook`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${ultra_admin_token}`
          }
        }
      );
      
      toast.success(response.data.message || 'Webhook başarıyla test edildi!');
    } catch (error) {
      console.error('Test webhook error:', error);
      toast.error(error.response?.data?.detail || 'Webhook testi başarısız oldu.');
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswordFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const previewBrandingChanges = () => {
    if (!originalSettings) {
      // Save CURRENT state before applying preview
      // Get the actual currently applied colors from context (what user sees now)
      const currentPrimaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim();
      const currentSecondaryColor = getComputedStyle(document.documentElement).getPropertyValue('--secondary-color').trim();
      
      setOriginalSettings({
        ...settings,
        primary_color: currentPrimaryColor || settings.primary_color,
        secondary_color: currentSecondaryColor || settings.secondary_color
      });
    }
    setIsPreviewing(true);
    applyBrandingSettings(settings);
    toast.success('🎨 Ön izleme aktif! Değişiklikleri görüyorsunuz.', {
      duration: 3000
    });
  };

  const cancelPreview = () => {
    if (originalSettings) {
      // Restore original settings to local state first
      const restoredSettings = { 
        ...settings, 
        primary_color: originalSettings.primary_color,
        secondary_color: originalSettings.secondary_color,
        logo_url: originalSettings.logo_url || '',
        favicon_url: originalSettings.favicon_url || '',
        custom_css: originalSettings.custom_css || '',
        footer_text: originalSettings.footer_text || '© 2025 AdvanceCash.io - Tüm hakları saklıdır'
      };
      
      setSettings(restoredSettings);
      
      // Apply to context (this will update UltraAdminLayout)
      applyBrandingSettings(restoredSettings);
      
      // Reset preview state
      setIsPreviewing(false);
      setOriginalSettings(null);
      setUnsavedChanges(false);
      
      toast.info('Ön izleme iptal edildi. Orijinal ayarlar geri yüklendi.', {
        duration: 3000
      });
    }
  };

  const resetToDefaults = () => {
    const defaultBrandingSettings = {
      primary_color: '#7C3AED',
      secondary_color: '#4F46E5',
      logo_url: '',
      favicon_url: '',
      custom_css: '',
      footer_text: '© 2025 AdvanceCash.io - Tüm hakları saklıdır'
    };
    
    // Update settings state
    setSettings(prev => ({ ...prev, ...defaultBrandingSettings }));
    setUnsavedChanges(true);
    
    // Save current settings as original if not already in preview
    if (!originalSettings) {
      setOriginalSettings({ ...settings });
    }
    
    // Apply immediately for visual feedback
    setIsPreviewing(true);
    applyBrandingSettings({ ...settings, ...defaultBrandingSettings });
    
    toast.success('🎨 Varsayılan renkler uygulandı! İnceleyip kaydedin veya geri alın.', {
      duration: 4000
    });
  };

  // Currency Management Functions
  const MAJOR_CURRENCIES = [
    { code: 'TRY', symbol: '₺', name: 'Türk Lirası' },
    { code: 'USD', symbol: '$', name: 'ABD Doları' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'İngiliz Sterlini' },
    { code: 'JPY', symbol: '¥', name: 'Japon Yeni' },
    { code: 'CHF', symbol: 'Fr', name: 'İsviçre Frangı' },
    { code: 'CAD', symbol: 'C$', name: 'Kanada Doları' },
    { code: 'AUD', symbol: 'A$', name: 'Avustralya Doları' },
    { code: 'CNY', symbol: '¥', name: 'Çin Yuanı' },
    { code: 'SEK', symbol: 'kr', name: 'İsveç Kronu' },
    { code: 'NOK', symbol: 'kr', name: 'Norveç Kronu' },
    { code: 'DKK', symbol: 'kr', name: 'Danimarka Kronu' },
    { code: 'PLN', symbol: 'zł', name: 'Polonya Zlotisi' },
    { code: 'CZK', symbol: 'Kč', name: 'Çek Kronu' },
    { code: 'HUF', symbol: 'Ft', name: 'Macar Forinti' },
    { code: 'RUB', symbol: '₽', name: 'Rus Rublesi' },
    { code: 'BRL', symbol: 'R$', name: 'Brezilya Reali' },
    { code: 'MXN', symbol: '$', name: 'Meksika Pesosu' },
    { code: 'INR', symbol: '₹', name: 'Hint Rupisi' },
    { code: 'KRW', symbol: '₩', name: 'Güney Kore Wonu' },
    { code: 'ZAR', symbol: 'R', name: 'Güney Afrika Randı' },
    { code: 'SGD', symbol: 'S$', name: 'Singapur Doları' },
    { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Doları' },
    { code: 'NZD', symbol: 'NZ$', name: 'Yeni Zelanda Doları' },
    { code: 'THB', symbol: '฿', name: 'Tayland Bahtı' },
    { code: 'MYR', symbol: 'RM', name: 'Malezya Ringgiti' },
    { code: 'IDR', symbol: 'Rp', name: 'Endonezya Rupisi' },
    { code: 'PHP', symbol: '₱', name: 'Filipin Pesosu' },
    { code: 'AED', symbol: 'د.إ', name: 'BAE Dirhemi' },
    { code: 'SAR', symbol: '﷼', name: 'Suudi Arabistan Riyali' }
  ];

  const fetchCurrencies = async () => {
    try {
      setLoadingCurrencies(true);
      // Use ultra-admin endpoint with automatic token injection via App.js interceptor
      const response = await axios.get(`${BACKEND_URL}/api/ultra-admin/currencies`);
      setCurrencies(response.data || []);
    } catch (error) {
      console.error('Error fetching currencies:', error);
      if (error.response?.status === 401) {
        toast.error('Ultra Admin oturumu süresi dolmuş. Lütfen tekrar giriş yapın.');
      } else {
        toast.error(error.response?.data?.detail || 'Para birimleri yüklenirken hata oluştu');
      }
    } finally {
      setLoadingCurrencies(false);
    }
  };

  const handleInitializeCurrencies = async () => {
    try {
      setProcessingCurrency(true);
      // Use ultra-admin endpoint with automatic token injection
      await axios.post(`${BACKEND_URL}/api/ultra-admin/currencies/initialize-major`);
      
      toast.success('Tüm major para birimleri başarıyla eklendi');
      fetchCurrencies();
    } catch (error) {
      console.error('Error initializing currencies:', error);
      if (error.response?.status === 401) {
        toast.error('Ultra Admin oturumu süresi dolmuş. Lütfen tekrar giriş yapın.');
      } else {
        toast.error(error.response?.data?.detail || 'Para birimleri eklenirken hata oluştu');
      }
    } finally {
      setProcessingCurrency(false);
    }
  };

  const handleSyncCurrenciesFromAPI = async () => {
    try {
      setProcessingCurrency(true);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/ultra-admin/currencies/sync-from-api`
      );
      
      const data = response.data;
      
      if (data.added > 0) {
        toast.success(
          `✅ ${data.added} yeni para birimi eklendi! (Toplam: ${data.total_in_db_after})`
        );
      } else {
        toast.info('ℹ️ Tüm API para birimleri zaten mevcut');
      }
      
      fetchCurrencies();
    } catch (error) {
      console.error('Error syncing currencies:', error);
      if (error.response?.status === 401) {
        toast.error('Ultra Admin oturumu süresi dolmuş. Lütfen tekrar giriş yapın.');
      } else {
        toast.error(error.response?.data?.detail || 'API senkronizasyonu başarısız');
      }
    } finally {
      setProcessingCurrency(false);
    }
  };

  const handleDeleteCurrency = async (currencyId, currencyCode, isDefault) => {
    if (isDefault) {
      toast.error('Varsayılan para birimleri silinemez');
      return;
    }

    if (!window.confirm(`${currencyCode} para birimini silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      // Use ultra-admin endpoint with automatic token injection
      await axios.delete(`${BACKEND_URL}/api/ultra-admin/currencies/${currencyId}`);
      toast.success('Para birimi silindi');
      fetchCurrencies();
    } catch (error) {
      console.error('Error deleting currency:', error);
      if (error.response?.status === 401) {
        toast.error('Ultra Admin oturumu süresi dolmuş. Lütfen tekrar giriş yapın.');
      } else {
        toast.error(error.response?.data?.detail || 'Para birimi silinirken hata oluştu');
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.txt')) {
        toast.error('Sadece .txt dosyaları desteklenmektedir');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleImportFile = async () => {
    if (!selectedFile) {
      toast.error('Lütfen bir dosya seçin');
      return;
    }

    try {
      setImportingFile(true);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/ultra-admin/currencies/import`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const result = response.data;
      
      // Show success message
      let message = `✅ ${result.added} para birimi eklendi`;
      if (result.skipped > 0) {
        message += `, ${result.skipped} zaten mevcut`;
      }
      
      toast.success(message, { duration: 5000 });
      
      // Show errors if any
      if (result.errors && result.errors.length > 0) {
        toast.warning(`⚠️ ${result.error_count} satırda hata bulundu`, { duration: 5000 });
        console.log('Import errors:', result.errors);
      }
      
      // Reset file input
      setSelectedFile(null);
      document.getElementById('currency-file-input').value = '';
      
      // Refresh currencies list
      fetchCurrencies();
      
    } catch (error) {
      console.error('Error importing file:', error);
      if (error.response?.status === 401) {
        toast.error('Ultra Admin oturumu süresi dolmuş. Lütfen tekrar giriş yapın.');
      } else {
        toast.error(error.response?.data?.detail || 'Dosya import edilirken hata oluştu');
      }
    } finally {
      setImportingFile(false);
    }
  };

  const handleAddCurrency = async () => {
    // Validate
    if (!newCurrency.code || !newCurrency.symbol) {
      toast.error('Kod ve Sembol alanları zorunludur');
      return;
    }

    if (newCurrency.code.length !== 3) {
      toast.error('Para birimi kodu 3 karakter olmalı');
      return;
    }

    try {
      setProcessingCurrency(true);
      
      await axios.post(
        `${BACKEND_URL}/api/ultra-admin/currencies`,
        {
          code: newCurrency.code.toUpperCase(),
          symbol: newCurrency.symbol,
          name: newCurrency.name || newCurrency.code.toUpperCase()
        }
      );
      
      toast.success(`${newCurrency.code.toUpperCase()} para birimi eklendi`);
      
      // Reset form and close modal
      setNewCurrency({ code: '', symbol: '', name: '' });
      setShowAddCurrencyModal(false);
      
      // Refresh list
      fetchCurrencies();
      
    } catch (error) {
      console.error('Error adding currency:', error);
      if (error.response?.status === 401) {
        toast.error('Ultra Admin oturumu süresi dolmuş. Lütfen tekrar giriş yapın.');
      } else {
        toast.error(error.response?.data?.detail || 'Para birimi eklenirken hata oluştu');
      }
    } finally {
      setProcessingCurrency(false);
    }
  };

  const handleExportCurrencies = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/ultra-admin/currencies/export`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'currencies_export.txt');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Para birimleri export edildi');
      
    } catch (error) {
      console.error('Error exporting currencies:', error);
      toast.error('Export işlemi sırasında hata oluştu');
    }
  };

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <GlobeIcon className="w-5 h-5 mr-2 text-blue-600" />
          Platform Bilgileri
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Platform Adı
            </label>
            <Input
              value={settings.platform_name}
              onChange={(e) => handleSettingChange('platform_name', e.target.value)}
              placeholder="AdvanceCash.io"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Platform Sloganı
            </label>
            <Input
              value={settings.platform_tagline}
              onChange={(e) => handleSettingChange('platform_tagline', e.target.value)}
              placeholder="Çalışan Avans Yönetim Platformu"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Destek Email
            </label>
            <Input
              type="email"
              value={settings.support_email}
              onChange={(e) => handleSettingChange('support_email', e.target.value)}
              placeholder="support@advancecash.io"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Destek Telefon
            </label>
            <Input
              value={settings.support_phone}
              onChange={(e) => handleSettingChange('support_phone', e.target.value)}
              placeholder="+90 555 123 4567"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Varsayılan Dil
            </label>
            <select
              className="w-full h-10 px-3 border border-gray-300 rounded-md"
              value={settings.default_language}
              onChange={(e) => handleSettingChange('default_language', e.target.value)}
            >
              <option value="tr">Türkçe</option>
              <option value="en">English</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Varsayılan Saat Dilimi
            </label>
            <select
              className="w-full h-10 px-3 border border-gray-300 rounded-md"
              value={settings.default_timezone}
              onChange={(e) => handleSettingChange('default_timezone', e.target.value)}
            >
              <option value="Europe/Istanbul">Europe/Istanbul (GMT+3)</option>
              <option value="UTC">UTC (GMT+0)</option>
              <option value="America/New_York">America/New_York (GMT-5)</option>
            </select>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <AlertTriangleIcon className="w-5 h-5 mr-2 text-orange-600" />
          Bakım Modu
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <p className="font-medium text-gray-900">Bakım Modu</p>
              <p className="text-sm text-gray-600">Platform bakıma alındığında kullanıcılar giriş yapamaz</p>
            </div>
            <button
              onClick={() => handleSettingChange('maintenance_mode', !settings.maintenance_mode)}
              className="ml-4"
            >
              {settings.maintenance_mode ? (
                <ToggleRightIcon className="w-8 h-8 text-orange-600" />
              ) : (
                <ToggleLeftIcon className="w-8 h-8 text-gray-400" />
              )}
            </button>
          </div>
          {settings.maintenance_mode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bakım Mesajı
              </label>
              <textarea
                className="w-full h-20 px-3 py-2 border border-gray-300 rounded-md"
                value={settings.maintenance_message}
                onChange={(e) => handleSettingChange('maintenance_message', e.target.value)}
                placeholder="Kullanıcılara gösterilecek mesaj"
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <LockIcon className="w-5 h-5 mr-2 text-red-600" />
          Şifre Politikaları
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Şifre Uzunluğu
            </label>
            <Input
              type="number"
              min="6"
              max="32"
              value={settings.password_min_length}
              onChange={(e) => handleSettingChange('password_min_length', parseInt(e.target.value))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Şifre Geçerlilik Süresi (Gün)
            </label>
            <Input
              type="number"
              min="0"
              value={settings.password_expiry_days}
              onChange={(e) => handleSettingChange('password_expiry_days', parseInt(e.target.value))}
            />
            <p className="text-xs text-gray-500 mt-1">0 = Sınırsız</p>
          </div>
        </div>
        
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700">Büyük harf zorunluluğu</span>
            <button onClick={() => handleSettingChange('password_require_uppercase', !settings.password_require_uppercase)}>
              {settings.password_require_uppercase ? (
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
              ) : (
                <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
              )}
            </button>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700">Küçük harf zorunluluğu</span>
            <button onClick={() => handleSettingChange('password_require_lowercase', !settings.password_require_lowercase)}>
              {settings.password_require_lowercase ? (
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
              ) : (
                <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
              )}
            </button>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700">Rakam zorunluluğu</span>
            <button onClick={() => handleSettingChange('password_require_numbers', !settings.password_require_numbers)}>
              {settings.password_require_numbers ? (
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
              ) : (
                <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
              )}
            </button>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700">Özel karakter zorunluluğu (!@#$%)</span>
            <button onClick={() => handleSettingChange('password_require_special', !settings.password_require_special)}>
              {settings.password_require_special ? (
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
              ) : (
                <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
              )}
            </button>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <ShieldIcon className="w-5 h-5 mr-2 text-red-600" />
          İki Faktörlü Kimlik Doğrulama (2FA)
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <p className="font-medium text-gray-900">2FA'yı Etkinleştir</p>
              <p className="text-sm text-gray-600">Kullanıcılar 2FA kullanabilir</p>
            </div>
            <button onClick={() => handleSettingChange('enable_2fa', !settings.enable_2fa)}>
              {settings.enable_2fa ? (
                <ToggleRightIcon className="w-8 h-8 text-green-600" />
              ) : (
                <ToggleLeftIcon className="w-8 h-8 text-gray-400" />
              )}
            </button>
          </div>
          {settings.enable_2fa && (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <p className="font-medium text-gray-900">Admin'ler için 2FA Zorunlu</p>
                <p className="text-sm text-gray-600">Ultra Admin ve Admin rolleri için zorunlu</p>
              </div>
              <button onClick={() => handleSettingChange('force_2fa_for_admins', !settings.force_2fa_for_admins)}>
                {settings.force_2fa_for_admins ? (
                  <ToggleRightIcon className="w-8 h-8 text-green-600" />
                ) : (
                  <ToggleLeftIcon className="w-8 h-8 text-gray-400" />
                )}
              </button>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <ClockIcon className="w-5 h-5 mr-2 text-red-600" />
          Session & Login Güvenliği
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Session Timeout (Dakika)
            </label>
            <Input
              type="number"
              min="15"
              value={settings.session_timeout_minutes}
              onChange={(e) => handleSettingChange('session_timeout_minutes', parseInt(e.target.value))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maksimum Giriş Denemesi
            </label>
            <Input
              type="number"
              min="3"
              max="10"
              value={settings.max_login_attempts}
              onChange={(e) => handleSettingChange('max_login_attempts', parseInt(e.target.value))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kilitleme Süresi (Dakika)
            </label>
            <Input
              type="number"
              min="5"
              value={settings.lockout_duration_minutes}
              onChange={(e) => handleSettingChange('lockout_duration_minutes', parseInt(e.target.value))}
            />
          </div>
        </div>
      </Card>

      <Card className="p-6 border-2 border-purple-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <KeyIcon className="w-5 h-5 mr-2 text-purple-600" />
          Passkey Yönetimi (Kalıcı Silme İçin)
        </h3>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-3">
            <AlertTriangleIcon className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-purple-800">
              <p className="font-semibold mb-1">⚠️ Dikkat: Sadece Kalıcı Silme İçin</p>
              <p>Bu passkey, tenant veya kullanıcıları <strong>veritabanından KALICI olarak silmek</strong> için kullanılır.</p>
              <ul className="list-disc ml-4 mt-2 space-y-1">
                <li>Geçerlilik: <strong>90 saniye</strong></li>
                <li>Kullanım: <strong>Tek seferlik</strong></li>
                <li>Yetki: <strong>Sadece Ultra Admin</strong></li>
              </ul>
            </div>
          </div>
        </div>
        <PasskeyGenerator />
      </Card>

      <Card className="p-6 border-2 border-red-200 bg-red-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <DatabaseIcon className="w-5 h-5 mr-2 text-red-600" />
          Test Verilerini Temizle
        </h3>
        <div className="bg-white border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-3">
            <AlertTriangleIcon className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-800">
              <p className="font-semibold mb-1">🗑️ Tehlikeli İşlem!</p>
              <p>Bu buton aşağıdaki test verilerini <strong>KALICI OLARAK</strong> silecektir:</p>
              <ul className="list-disc ml-4 mt-2 space-y-1">
                <li>Email'inde "test" veya "demo" geçen kullanıcılar</li>
                <li>Email'inde "example.com" geçen kullanıcılar</li>
                <li>Company name'inde "test" veya "demo" geçen şirketler</li>
                <li>Bu şirketlere ait tüm avanslar, davetler ve doğrulamalar</li>
              </ul>
              <p className="mt-2 font-semibold text-red-700">⚠️ Bu işlem geri alınamaz!</p>
            </div>
          </div>
        </div>
        <Button
          variant="destructive"
          className="bg-red-600 hover:bg-red-700"
          onClick={async () => {
            if (window.confirm('⚠️ TÜM TEST VERİLERİNİ SİLMEK İSTEDİĞİNİZDEN EMİN MİSİNİZ?\n\nBu işlem geri alınamaz!')) {
              try {
                const token = localStorage.getItem('ultra_admin_token');
                const response = await axios.post(
                  `${BACKEND_URL}/api/ultra-admin/delete-test-data`,
                  {},
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success(`✅ Test verileri silindi: ${response.data.deleted.users} kullanıcı, ${response.data.deleted.companies} şirket`);
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Test verileri silinirken hata oluştu');
              }
            }
          }}
        >
          <DatabaseIcon className="w-4 h-4 mr-2" />
          Tüm Test Verilerini Sil
        </Button>
      </Card>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <ServerIcon className="w-5 h-5 mr-2 text-green-600" />
            SMTP Konfigürasyonu
          </h3>
          <Button variant="outline" onClick={testEmailSettings}>
            <MailIcon className="w-4 h-4 mr-2" />
            Test Email Gönder
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMTP Host
            </label>
            <Input
              value={settings.smtp_host}
              onChange={(e) => handleSettingChange('smtp_host', e.target.value)}
              placeholder="smtp.gmail.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMTP Port
            </label>
            <Input
              type="number"
              value={settings.smtp_port}
              onChange={(e) => handleSettingChange('smtp_port', parseInt(e.target.value))}
              placeholder="587"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMTP Kullanıcı Adı
            </label>
            <Input
              value={settings.smtp_username}
              onChange={(e) => handleSettingChange('smtp_username', e.target.value)}
              placeholder="user@gmail.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMTP Şifre
            </label>
            <div className="relative">
              <Input
                type={showPasswordFields.smtp_password ? 'text' : 'password'}
                value={settings.smtp_password}
                onChange={(e) => handleSettingChange('smtp_password', e.target.value)}
                placeholder="••••••••"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('smtp_password')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                {showPasswordFields.smtp_password ? (
                  <EyeOffIcon className="w-4 h-4 text-gray-400" />
                ) : (
                  <EyeIcon className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Şifreleme
            </label>
            <select
              className="w-full h-10 px-3 border border-gray-300 rounded-md"
              value={settings.smtp_encryption}
              onChange={(e) => handleSettingChange('smtp_encryption', e.target.value)}
            >
              <option value="none">Yok</option>
              <option value="tls">TLS</option>
              <option value="ssl">SSL</option>
            </select>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <MailIcon className="w-5 h-5 mr-2 text-green-600" />
          Email Gönderen Bilgileri
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gönderen Email Adresi
            </label>
            <Input
              type="email"
              value={settings.email_from_address}
              onChange={(e) => handleSettingChange('email_from_address', e.target.value)}
              placeholder="noreply@advancecash.io"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gönderen İsim
            </label>
            <Input
              value={settings.email_from_name}
              onChange={(e) => handleSettingChange('email_from_name', e.target.value)}
              placeholder="AdvanceCash.io"
            />
          </div>
        </div>
      </Card>
    </div>
  );

  const renderPaymentSettings = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <CreditCardIcon className="w-5 h-5 mr-2 text-purple-600" />
          Stripe Entegrasyonu
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stripe Publishable Key
            </label>
            <Input
              value={settings.stripe_publishable_key}
              onChange={(e) => handleSettingChange('stripe_publishable_key', e.target.value)}
              placeholder="pk_live_..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stripe Secret Key
            </label>
            <div className="relative">
              <Input
                type={showPasswordFields.stripe_secret_key ? 'text' : 'password'}
                value={settings.stripe_secret_key}
                onChange={(e) => handleSettingChange('stripe_secret_key', e.target.value)}
                placeholder="sk_live_..."
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('stripe_secret_key')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                {showPasswordFields.stripe_secret_key ? (
                  <EyeOffIcon className="w-4 h-4 text-gray-400" />
                ) : (
                  <EyeIcon className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <BanknoteIcon className="w-5 h-5 mr-2 text-purple-600" />
          Ödeme Ayarları
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Varsayılan Para Birimi
            </label>
            <select
              className="w-full h-10 px-3 border border-gray-300 rounded-md"
              value={settings.default_currency}
              onChange={(e) => handleSettingChange('default_currency', e.target.value)}
            >
              <option value="TRY">TRY (₺)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vergi Oranı (%)
            </label>
            <Input
              type="number"
              min="0"
              max="100"
              value={settings.tax_rate}
              onChange={(e) => handleSettingChange('tax_rate', parseFloat(e.target.value))}
            />
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <ClockIcon className="w-5 h-5 mr-2 text-purple-600" />
          Deneme Süresi
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <p className="font-medium text-gray-900">Deneme Süresini Etkinleştir</p>
              <p className="text-sm text-gray-600">Yeni kullanıcılar ücretsiz deneme süresi alır</p>
            </div>
            <button onClick={() => handleSettingChange('enable_trial', !settings.enable_trial)}>
              {settings.enable_trial ? (
                <ToggleRightIcon className="w-8 h-8 text-green-600" />
              ) : (
                <ToggleLeftIcon className="w-8 h-8 text-gray-400" />
              )}
            </button>
          </div>
          {settings.enable_trial && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deneme Süresi (Gün)
              </label>
              <Input
                type="number"
                min="1"
                max="90"
                value={settings.trial_days}
                onChange={(e) => handleSettingChange('trial_days', parseInt(e.target.value))}
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );

  const renderSystemSettings = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <DatabaseIcon className="w-5 h-5 mr-2 text-orange-600" />
          Database Yedekleme
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <p className="font-medium text-gray-900">Otomatik Yedekleme</p>
              <p className="text-sm text-gray-600">Veritabanı otomatik olarak yedeklenir</p>
            </div>
            <button onClick={() => handleSettingChange('database_backup_enabled', !settings.database_backup_enabled)}>
              {settings.database_backup_enabled ? (
                <ToggleRightIcon className="w-8 h-8 text-green-600" />
              ) : (
                <ToggleLeftIcon className="w-8 h-8 text-gray-400" />
              )}
            </button>
          </div>
          {settings.database_backup_enabled && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Yedekleme Sıklığı
                </label>
                <select
                  className="w-full h-10 px-3 border border-gray-300 rounded-md"
                  value={settings.backup_frequency}
                  onChange={(e) => handleSettingChange('backup_frequency', e.target.value)}
                >
                  <option value="hourly">Saatlik</option>
                  <option value="daily">Günlük</option>
                  <option value="weekly">Haftalık</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Yedekleme Saklama Süresi (Gün)
                </label>
                <Input
                  type="number"
                  min="1"
                  value={settings.backup_retention_days}
                  onChange={(e) => handleSettingChange('backup_retention_days', parseInt(e.target.value))}
                />
              </div>
            </>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <ActivityIcon className="w-5 h-5 mr-2 text-orange-600" />
          Log Ayarları
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Log Saklama Süresi (Gün)
          </label>
          <Input
            type="number"
            min="1"
            value={settings.log_retention_days}
            onChange={(e) => handleSettingChange('log_retention_days', parseInt(e.target.value))}
          />
          <p className="text-xs text-gray-500 mt-1">Eski loglar otomatik olarak silinir</p>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <ZapIcon className="w-5 h-5 mr-2 text-orange-600" />
          Cache & Performance
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <p className="font-medium text-gray-900">Cache Sistemini Etkinleştir</p>
              <p className="text-sm text-gray-600">Performans artışı için önbellek kullan</p>
            </div>
            <button onClick={() => handleSettingChange('cache_enabled', !settings.cache_enabled)}>
              {settings.cache_enabled ? (
                <ToggleRightIcon className="w-8 h-8 text-green-600" />
              ) : (
                <ToggleLeftIcon className="w-8 h-8 text-gray-400" />
              )}
            </button>
          </div>
          {settings.cache_enabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cache TTL (Saniye)
              </label>
              <Input
                type="number"
                min="60"
                value={settings.cache_ttl_seconds}
                onChange={(e) => handleSettingChange('cache_ttl_seconds', parseInt(e.target.value))}
              />
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <WifiIcon className="w-5 h-5 mr-2 text-orange-600" />
          API Rate Limiting
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Dakika Başına İstek Limiti
          </label>
          <Input
            type="number"
            min="10"
            value={settings.api_rate_limit_per_minute}
            onChange={(e) => handleSettingChange('api_rate_limit_per_minute', parseInt(e.target.value))}
          />
          <p className="text-xs text-gray-500 mt-1">Her IP için dakikada maksimum istek sayısı</p>
        </div>
      </Card>
    </div>
  );

  const renderBrandingSettings = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <PaletteIcon className="w-5 h-5 mr-2 text-pink-600" />
          Renk Şeması
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Color
            </label>
            <div className="flex items-center space-x-3">
              <Input
                type="color"
                value={settings.primary_color}
                onChange={(e) => handleSettingChange('primary_color', e.target.value)}
                className="w-20 h-10"
              />
              <Input
                value={settings.primary_color}
                onChange={(e) => handleSettingChange('primary_color', e.target.value)}
                placeholder="#7C3AED"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secondary Color
            </label>
            <div className="flex items-center space-x-3">
              <Input
                type="color"
                value={settings.secondary_color}
                onChange={(e) => handleSettingChange('secondary_color', e.target.value)}
                className="w-20 h-10"
              />
              <Input
                value={settings.secondary_color}
                onChange={(e) => handleSettingChange('secondary_color', e.target.value)}
                placeholder="#4F46E5"
              />
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <EyeIcon className="w-5 h-5 mr-2 text-pink-600" />
          Ön İzleme & Kontroller
        </h3>
        <div className="space-y-4">
          {isPreviewing && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center">
                <InfoIcon className="w-5 h-5 text-blue-600 mr-2" />
                <span className="text-blue-800 font-medium">Ön izleme modu aktif</span>
              </div>
              <p className="text-blue-700 text-sm mt-1">
                Şu anda branding değişikliklerinin ön izlemesini görüyorsunuz. Kaydetmek veya iptal etmek için aşağıdaki butonları kullanın.
              </p>
            </div>
          )}
          
          <div className="flex flex-wrap gap-3">
            <Button 
              variant="outline" 
              onClick={previewBrandingChanges}
              disabled={isPreviewing}
              className="flex items-center"
            >
              <EyeIcon className="w-4 h-4 mr-2" />
              Ön İzleme
            </Button>
            
            {isPreviewing && (
              <Button 
                variant="outline" 
                onClick={cancelPreview}
                className="flex items-center text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                <EyeOffIcon className="w-4 h-4 mr-2" />
                Geri Al
              </Button>
            )}
            
            <Button 
              variant="outline" 
              onClick={resetToDefaults}
              className="flex items-center text-gray-600 border-gray-300 hover:bg-gray-50"
            >
              <RefreshCwIcon className="w-4 h-4 mr-2" />
              Varsayılan Renklere Dön
            </Button>
            
            {(isPreviewing || unsavedChanges) && (
              <Button 
                onClick={saveSettings}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700 flex items-center"
              >
                {saving ? (
                  <>
                    <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <SaveIcon className="w-4 h-4 mr-2" />
                    Kaydet
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <ImageIcon className="w-5 h-5 mr-2 text-pink-600" />
          Logo & Favicon
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo URL
            </label>
            <div className="flex items-center space-x-3">
              <Input
                value={settings.logo_url}
                onChange={(e) => handleSettingChange('logo_url', e.target.value)}
                placeholder="https://example.com/logo.png"
              />
              <Button variant="outline">
                <UploadIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Favicon URL
            </label>
            <div className="flex items-center space-x-3">
              <Input
                value={settings.favicon_url}
                onChange={(e) => handleSettingChange('favicon_url', e.target.value)}
                placeholder="https://example.com/favicon.ico"
              />
              <Button variant="outline">
                <UploadIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <CodeIcon className="w-5 h-5 mr-2 text-pink-600" />
          Custom CSS
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Özel CSS Kodları
          </label>
          <textarea
            className="w-full h-40 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
            value={settings.custom_css}
            onChange={(e) => handleSettingChange('custom_css', e.target.value)}
            placeholder=".custom-class { color: #000; }"
          />
          <p className="text-xs text-gray-500 mt-1">Dikkatli kullanın! Hatalı CSS site görünümünü bozabilir.</p>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <InfoIcon className="w-5 h-5 mr-2 text-pink-600" />
          Footer
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Footer Metni
          </label>
          <Input
            value={settings.footer_text}
            onChange={(e) => handleSettingChange('footer_text', e.target.value)}
            placeholder="© 2025 AdvanceCash.io - Tüm hakları saklıdır"
          />
        </div>
      </Card>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <BellIcon className="w-5 h-5 mr-2 text-yellow-600" />
          Bildirim Kanalları
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <p className="font-medium text-gray-900">Email Bildirimleri</p>
              <p className="text-sm text-gray-600">Kullanıcılara email gönder</p>
            </div>
            <button onClick={() => handleSettingChange('enable_email_notifications', !settings.enable_email_notifications)}>
              {settings.enable_email_notifications ? (
                <ToggleRightIcon className="w-8 h-8 text-green-600" />
              ) : (
                <ToggleLeftIcon className="w-8 h-8 text-gray-400" />
              )}
            </button>
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <p className="font-medium text-gray-900">SMS Bildirimleri</p>
              <p className="text-sm text-gray-600">Kullanıcılara SMS gönder</p>
            </div>
            <button onClick={() => handleSettingChange('enable_sms_notifications', !settings.enable_sms_notifications)}>
              {settings.enable_sms_notifications ? (
                <ToggleRightIcon className="w-8 h-8 text-green-600" />
              ) : (
                <ToggleLeftIcon className="w-8 h-8 text-gray-400" />
              )}
            </button>
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <p className="font-medium text-gray-900">Push Notifications</p>
              <p className="text-sm text-gray-600">Browser push bildirimleri</p>
            </div>
            <button onClick={() => handleSettingChange('enable_push_notifications', !settings.enable_push_notifications)}>
              {settings.enable_push_notifications ? (
                <ToggleRightIcon className="w-8 h-8 text-green-600" />
              ) : (
                <ToggleLeftIcon className="w-8 h-8 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <ZapIcon className="w-5 h-5 mr-2 text-yellow-600" />
            Webhook
          </h3>
          <Button variant="outline" onClick={testWebhook}>
            <ActivityIcon className="w-4 h-4 mr-2" />
            Webhook Test Et
          </Button>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Webhook URL
          </label>
          <Input
            value={settings.webhook_url}
            onChange={(e) => handleSettingChange('webhook_url', e.target.value)}
            placeholder="https://your-server.com/webhook"
          />
          <p className="text-xs text-gray-500 mt-1">Önemli olaylarda bu URL'e POST request gönderilir</p>
        </div>
      </Card>
    </div>
  );

  const renderCurrencySettings = () => (
    <div className="space-y-6">
      {/* Initialize Major Currencies */}
      {currencies.length === 0 && (
        <Card className="p-6 bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                <CoinsIcon className="w-5 h-5 mr-2 text-emerald-600" />
                Hızlı Başlangıç: Major Para Birimlerini Ekle
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                30 major world currency'yi (TRY, USD, EUR, GBP, JPY, vb.) tek tıkla sisteme ekleyin. 
                Şirketler bunları temel para birimi olarak seçebilecek.
              </p>
              <Button
                onClick={handleInitializeCurrencies}
                disabled={processingCurrency}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {processingCurrency ? (
                  <>
                    <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
                    Ekleniyor...
                  </>
                ) : (
                  <>
                    <CoinsIcon className="w-4 h-4 mr-2" />
                    Tüm Major Para Birimlerini Ekle (30)
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Import from TXT File */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
              <UploadIcon className="w-5 h-5 mr-2 text-blue-600" />
              TXT Dosyasından Para Birimi İçe Aktar
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Para birimlerini TXT dosyasından toplu olarak içe aktarın.
              <br />
              <span className="font-semibold">Format:</span> Her satırda <code className="bg-gray-200 px-1 rounded">KOD,SEMBOL</code> (Örnek: <code className="bg-gray-200 px-1 rounded">TRY,₺</code>)
            </p>
            
            <div className="flex items-center space-x-3">
              <input
                id="currency-file-input"
                type="file"
                accept=".txt"
                onChange={handleFileSelect}
                className="block text-sm text-gray-600
                  file:mr-4 file:py-2 file:px-4
                  file:rounded file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-100 file:text-blue-700
                  hover:file:bg-blue-200
                  cursor-pointer"
              />
              
              <Button
                onClick={handleImportFile}
                disabled={!selectedFile || importingFile}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {importingFile ? (
                  <>
                    <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
                    İçe Aktarılıyor...
                  </>
                ) : (
                  <>
                    <UploadIcon className="w-4 h-4 mr-2" />
                    İçe Aktar
                  </>
                )}
              </Button>
            </div>
            
            {selectedFile && (
              <p className="mt-2 text-sm text-gray-700">
                <span className="font-semibold">Seçilen dosya:</span> {selectedFile.name}
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Sync from Exchange Rate API */}
      <Card className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
              <RefreshCwIcon className="w-5 h-5 mr-2 text-purple-600" />
              API'den Para Birimi Senkronizasyonu
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Exchange Rate API'den <strong>165+ para birimi</strong> çekin ve eksik olanları otomatik ekleyin.
              <br />
              <span className="text-purple-700 font-medium">
                🌍 Global Platform: Tüm dünya para birimlerini destekleyin!
              </span>
            </p>
            
            <div className="bg-white rounded-lg p-3 mb-4 border border-purple-100">
              <div className="flex items-center space-x-2 text-xs text-gray-600">
                <InfoIcon className="w-4 h-4 text-purple-500 flex-shrink-0" />
                <span>
                  API otomatik olarak <strong>165 ülkenin para birimini</strong> kontrol eder ve database'de olmayan
                  para birimlerini ekler. Mevcut para birimlerine dokunulmaz.
                </span>
              </div>
            </div>

            <Button
              onClick={handleSyncCurrenciesFromAPI}
              disabled={processingCurrency}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {processingCurrency ? (
                <>
                  <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
                  Senkronize Ediliyor...
                </>
              ) : (
                <>
                  <RefreshCwIcon className="w-4 h-4 mr-2" />
                  API'den Para Birimlerini Senkronize Et
                </>
              )}
            </Button>

            <div className="mt-4 flex items-start space-x-2 bg-purple-100 border border-purple-200 rounded-lg p-3">
              <CheckCircleIcon className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-purple-800">
                <p className="font-semibold mb-1">Ne Zaman Kullanılmalı?</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>İlk kurulumda tüm dünya para birimlerini eklemek için</li>
                  <li>Yeni ülke para birimleri eklendiğinde güncellemek için</li>
                  <li>Database'de eksik para birimleri olduğunu fark ettiğinizde</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Currency List */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <CoinsIcon className="w-5 h-5 mr-2 text-emerald-600" />
              Mevcut Para Birimleri
            </h3>
            {currencies.length > 0 && (
              <Badge className="bg-emerald-100 text-emerald-700">
                {currencies.length} Para Birimi
              </Badge>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={handleExportCurrencies}
              variant="outline"
              size="sm"
              className="text-gray-700 border-gray-300"
            >
              <DownloadIcon className="w-4 h-4 mr-2" />
              Export (.txt)
            </Button>
            <Button
              onClick={() => setShowAddCurrencyModal(true)}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Yeni Para Birimi
            </Button>
          </div>
        </div>

        {loadingCurrencies ? (
          <div className="text-center py-12">
            <RefreshCwIcon className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
            <p className="text-gray-600">Para birimleri yükleniyor...</p>
          </div>
        ) : currencies.length === 0 ? (
          <div className="text-center py-12">
            <CoinsIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-2">Henüz para birimi eklenmemiş</p>
            <p className="text-sm text-gray-500">
              Yukarıdaki butonu kullanarak major para birimlerini ekleyebilirsiniz
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kod
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sembol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currencies.map((currency) => (
                  <tr key={currency.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {currency.code}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-lg text-gray-900">
                        {currency.symbol}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {currency.is_default ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Varsayılan
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          Özel
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {!currency.is_default && (
                        <Button
                          onClick={() => handleDeleteCurrency(currency.id, currency.code, currency.is_default)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Info Card */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-3">
          <InfoIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-2">💡 Para Birimi Hakkında</p>
            <ul className="list-disc ml-4 space-y-1">
              <li>Buradan eklenen para birimleri tüm şirketler tarafından kullanılabilir</li>
              <li>Her şirket kendi temel para birimini bu listeden seçebilir</li>
              <li>Varsayılan para birimleri (TRY, USD, EUR) silinemez</li>
              <li>Para birimi silmek mevcut şirket ayarlarını etkilemez</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Add Currency Modal */}
      {showAddCurrencyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <PlusIcon className="w-5 h-5 mr-2 text-emerald-600" />
                Yeni Para Birimi Ekle
              </h3>
              <button
                onClick={() => {
                  setShowAddCurrencyModal(false);
                  setNewCurrency({ code: '', symbol: '', name: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Para Birimi Kodu <span className="text-red-500">*</span>
                </label>
                <Input
                  value={newCurrency.code}
                  onChange={(e) => setNewCurrency({ ...newCurrency, code: e.target.value.toUpperCase() })}
                  placeholder="TRY"
                  maxLength={3}
                  className="uppercase"
                />
                <p className="text-xs text-gray-500 mt-1">3 karakter (Örn: TRY, USD, EUR)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sembol <span className="text-red-500">*</span>
                </label>
                <Input
                  value={newCurrency.symbol}
                  onChange={(e) => setNewCurrency({ ...newCurrency, symbol: e.target.value })}
                  placeholder="₺"
                />
                <p className="text-xs text-gray-500 mt-1">Para birimi sembolü (Örn: ₺, $, €)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  İsim (Opsiyonel)
                </label>
                <Input
                  value={newCurrency.name}
                  onChange={(e) => setNewCurrency({ ...newCurrency, name: e.target.value })}
                  placeholder="Türk Lirası"
                />
                <p className="text-xs text-gray-500 mt-1">Boş bırakılırsa kod kullanılır</p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                onClick={() => {
                  setShowAddCurrencyModal(false);
                  setNewCurrency({ code: '', symbol: '', name: '' });
                }}
                variant="outline"
              >
                İptal
              </Button>
              <Button
                onClick={handleAddCurrency}
                disabled={processingCurrency || !newCurrency.code || !newCurrency.symbol}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {processingCurrency ? (
                  <>
                    <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
                    Ekleniyor...
                  </>
                ) : (
                  <>
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Ekle
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderIntegrationSettings = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <MessageSquareIcon className="w-5 h-5 mr-2 text-indigo-600" />
          WhatsApp Business API
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            WhatsApp Business API Key
          </label>
          <div className="relative">
            <Input
              type={showPasswordFields.whatsapp_business_api_key ? 'text' : 'password'}
              value={settings.whatsapp_business_api_key}
              onChange={(e) => handleSettingChange('whatsapp_business_api_key', e.target.value)}
              placeholder="waba_..."
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('whatsapp_business_api_key')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              {showPasswordFields.whatsapp_business_api_key ? (
                <EyeOffIcon className="w-4 h-4 text-gray-400" />
              ) : (
                <EyeIcon className="w-4 h-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <MessageSquareIcon className="w-5 h-5 mr-2 text-indigo-600" />
          SMS Provider
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMS Sağlayıcı
            </label>
            <select
              className="w-full h-10 px-3 border border-gray-300 rounded-md"
              value={settings.sms_provider}
              onChange={(e) => handleSettingChange('sms_provider', e.target.value)}
            >
              <option value="none">Seçiniz</option>
              <option value="twilio">Twilio</option>
              <option value="netgsm">NetGSM</option>
              <option value="iletimerkezi">İletimerkezi</option>
            </select>
          </div>
          {settings.sms_provider !== 'none' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMS API Key
              </label>
              <div className="relative">
                <Input
                  type={showPasswordFields.sms_api_key ? 'text' : 'password'}
                  value={settings.sms_api_key}
                  onChange={(e) => handleSettingChange('sms_api_key', e.target.value)}
                  placeholder="API Key"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('sms_api_key')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showPasswordFields.sms_api_key ? (
                    <EyeOffIcon className="w-4 h-4 text-gray-400" />
                  ) : (
                    <EyeIcon className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <ActivityIcon className="w-5 h-5 mr-2 text-indigo-600" />
          Analytics
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <p className="font-medium text-gray-900">Analytics'i Etkinleştir</p>
              <p className="text-sm text-gray-600">Google Analytics entegrasyonu</p>
            </div>
            <button onClick={() => handleSettingChange('analytics_enabled', !settings.analytics_enabled)}>
              {settings.analytics_enabled ? (
                <ToggleRightIcon className="w-8 h-8 text-green-600" />
              ) : (
                <ToggleLeftIcon className="w-8 h-8 text-gray-400" />
              )}
            </button>
          </div>
          {settings.analytics_enabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Google Analytics ID
              </label>
              <Input
                value={settings.google_analytics_id}
                onChange={(e) => handleSettingChange('google_analytics_id', e.target.value)}
                placeholder="G-XXXXXXXXXX"
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );

  const renderCategoryContent = () => {
    switch (activeCategory) {
      case 'general':
        return renderGeneralSettings();
      case 'security':
        return renderSecuritySettings();
      case 'email':
        return renderEmailSettings();
      case 'payment':
        return renderPaymentSettings();
      case 'system':
        return renderSystemSettings();
      case 'branding':
        return renderBrandingSettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'currencies':
        return renderCurrencySettings();
      case 'integrations':
        return renderIntegrationSettings();
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Platform Ayarları</h2>
            <p className="text-gray-600 mt-1">Sistem geneli konfigürasyon ve ayarlar</p>
          </div>
          <div className="flex items-center space-x-3">
            {unsavedChanges && (
              <Badge className="bg-orange-100 text-orange-700">
                <AlertTriangleIcon className="w-3 h-3 mr-1" />
                Kaydedilmemiş Değişiklikler
              </Badge>
            )}
            <Button 
              variant="outline" 
              onClick={loadSettings}
              disabled={loading}
            >
              <RefreshCwIcon className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Yenile
            </Button>
            <Button 
              onClick={saveSettings}
              disabled={saving || !unsavedChanges}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? (
                <>
                  <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <SaveIcon className="w-4 h-4 mr-2" />
                  Değişiklikleri Kaydet
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar Categories */}
        <div className="col-span-3">
          <Card className="p-4">
            <div className="space-y-1">
              {CATEGORIES.map((category) => {
                const Icon = category.icon;
                const isActive = activeCategory === category.id;
                
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? category.bg + ' ' + category.color + ' font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${isActive ? category.color : 'text-gray-400'}`} />
                    <span className="text-sm">{category.name}</span>
                  </button>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Content Area */}
        <div className="col-span-9">
          {loading ? (
            <Card className="p-12">
              <div className="text-center">
                <RefreshCwIcon className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
                <p className="text-gray-600">Ayarlar yükleniyor...</p>
              </div>
            </Card>
          ) : (
            renderCategoryContent()
          )}
        </div>
      </div>
    </div>
  );
};

export default UltraPlatformSettings;
